<template>
  <div class="p-6">
    <h3 class="text-lg font-semibold mb-6 dark:text-white">
      New GBP Campaign
    </h3>

    <UiControl id="campaign-name" label="Campaign Name">
      <UiInput v-model="form.name" :placeholder="defaultName" size="sm" />
    </UiControl>

    <div class="grid grid-cols-2 gap-4">
      <UiControl id="llm-provider" label="LLM Provider">
        <UiSelect
          id="llm-provider"
          v-model="form.llm_provider"
          :options="llmOptions"
          size="sm" />
      </UiControl>

      <UiControl id="image-provider" label="Image Provider">
        <UiSelect
          id="image-provider"
          v-model="form.image_provider"
          :options="imageOptions"
          size="sm" />
      </UiControl>
    </div>

    <div class="grid grid-cols-2 gap-4">
      <UiControl id="post-type" label="Post Type">
        <UiRadioGroup
          id="post-type"
          v-model="form.post_type"
          :options="postTypeOptions"
          position="horizontal"
          size="sm" />
      </UiControl>

      <UiControl id="cta-type" label="CTA">
        <UiRadioGroup
          id="cta-type"
          v-model="form.cta_type"
          :options="ctaOptions"
          position="horizontal"
          size="sm" />
      </UiControl>
    </div>

    <div class="grid grid-cols-3 gap-4">
      <UiControl id="date-start" label="Start Date">
        <UiInput v-model="form.date_range_start" type="date" size="sm" />
      </UiControl>

      <UiControl id="date-end" label="End Date">
        <UiInput v-model="form.date_range_end" type="date" size="sm" />
      </UiControl>

      <UiControl id="post-count" label="Number of Posts">
        <UiSelect
          id="post-count"
          v-model="form.target_posts_count"
          :options="postCountOptions"
          size="sm" />
      </UiControl>
    </div>

    <UiControl id="keywords" label="Keywords">
      <div class="flex flex-wrap gap-2 mb-2" v-if="form.keywords.length">
        <span
          v-for="(kw, i) in form.keywords"
          :key="i"
          class="bg-primary-100 dark:bg-primary-900 text-primary-800 dark:text-primary-200 px-2.5 py-1 rounded-full text-xs font-medium flex items-center gap-1.5">
          {{ kw }}
          <button @click="removeKeyword(i)" class="text-primary-500 hover:text-red-500 text-sm leading-none">&times;</button>
        </span>
      </div>
      <div class="flex gap-2">
        <UiInput v-model="newKeyword" placeholder="Add keyword..." size="sm" class="flex-1" @keydown.enter.prevent="addKeyword" />
        <button @click="addKeyword" class="btn btn-secondary btn-sm">
          Add
        </button>
      </div>
      <p v-if="form.keywords.length && effectiveCount > form.keywords.length" class="text-xs text-gray-500 mt-1">
        {{ form.keywords.length }} keywords will cycle across {{ effectiveCount }} posts
      </p>
    </UiControl>

    <details class="mb-5" open>
      <summary class="text-sm font-medium cursor-pointer dark:text-gray-300 text-gray-700 select-none">
        Image Overlay
      </summary>
      <div class="grid grid-cols-2 gap-4 mt-3">
        <UiControl id="bar-color" label="Bar Color" hint="Overrides business brand color">
          <div class="flex gap-2 items-center">
            <input v-model="form.bar_color" type="color" class="w-10 h-10 rounded cursor-pointer border-2 border-gray-200 dark:border-gray-600" />
            <UiInput v-model="form.bar_color" placeholder="Business default" size="sm" />
          </div>
        </UiControl>
        <UiControl id="bar-font" label="Bar Font" hint="Overrides business brand font">
          <UiSelect
            id="bar-font"
            v-model="form.bar_font"
            :options="fontOptions"
            size="sm" />
        </UiControl>
      </div>
    </details>

    <details class="mb-5">
      <summary class="text-sm font-medium cursor-pointer dark:text-gray-300 text-gray-700 select-none">
        UTM Parameters
      </summary>
      <div class="grid grid-cols-3 gap-3 mt-3">
        <UiControl id="utm-source" label="Source">
          <UiInput v-model="form.utm_params.source" size="sm" />
        </UiControl>
        <UiControl id="utm-medium" label="Medium">
          <UiInput v-model="form.utm_params.medium" size="sm" />
        </UiControl>
        <UiControl id="utm-campaign" label="Campaign">
          <UiInput v-model="form.utm_params.campaign" size="sm" />
        </UiControl>
      </div>
    </details>

    <UiControl id="custom-instructions" label="Custom Instructions" hint="Optional tone or style guidance for the LLM">
      <UiTextarea v-model="form.custom_instructions" size="sm" placeholder="e.g. Use a professional tone, emphasize emergency services..." />
    </UiControl>

    <div class="flex justify-end gap-3 mt-2 pt-4 border-t dark:border-gray-700">
      <button @click="$emit('on-close-panel')" class="btn btn-secondary btn-sm">
        Cancel
      </button>
      <button @click="submit" :disabled="submitting" class="btn btn-primary btn-sm">
        {{ submitting ? 'Creating...' : `Generate ${effectiveCount} Posts` }}
      </button>
    </div>

    <p v-if="error" class="text-red-500 text-sm mt-3">
      {{ error }}
    </p>
  </div>
</template>

<script setup>
import { ref, reactive, computed } from 'vue'
import { createGbpCampaign } from '~/javascript/api/gbp_campaigns'
import UiInput from '~/javascript/components/ui/form/input/input.vue'
import UiTextarea from '~/javascript/components/ui/form/textarea/textarea.vue'
import UiSelect from '~/javascript/components/ui/form/select/select.vue'
import UiRadioGroup from '~/javascript/components/ui/form/radio/radio_group.vue'
import UiControl from '~/javascript/components/ui/form/control/control.vue'

const props = defineProps({
  locationId: { type: String, default: '' },
  defaultKeywords: { type: Array, default: () => []}
})
const emit = defineEmits(['created', 'on-close-panel'])

const today = new Date().toISOString().split('T')[0]
const nextMonth = new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0]
const defaultName = `${new Date().toLocaleString('default', { month: 'long', year: 'numeric' })} Posts`

const llmOptions = [
  { id: 'anthropic', title: 'Anthropic (Claude)' },
  { id: 'openai', title: 'OpenAI (GPT-4o)' },
  { id: 'gemini', title: 'Gemini' }
]

const imageOptions = [
  { id: 'gemini_images', title: 'Gemini Images' },
  { id: 'banana', title: 'Banana' }
]

const postTypeOptions = [
  { value: 'update', label: 'Update' },
  { value: 'event', label: 'Event' }
]

const ctaOptions = [
  { value: 'call_now', label: 'Call Now' },
  { value: 'learn_more', label: 'Learn More' }
]

const fontOptions = [
  { id: '', title: 'Business Default' },
  { id: 'DejaVu-Sans', title: 'DejaVu Sans' },
  { id: 'DejaVu-Sans-Bold', title: 'DejaVu Sans Bold' },
  { id: 'DejaVu-Serif', title: 'DejaVu Serif' },
  { id: 'DejaVu-Serif-Bold', title: 'DejaVu Serif Bold' },
  { id: 'Lato-Regular', title: 'Lato' },
  { id: 'Lato-Bold', title: 'Lato Bold' },
  { id: 'Lato-Light', title: 'Lato Light' },
  { id: 'Nimbus-Sans-Regular', title: 'Nimbus Sans' },
  { id: 'Nimbus-Sans-Bold', title: 'Nimbus Sans Bold' },
  { id: 'Roboto', title: 'Roboto' },
  { id: 'Roboto-Bold', title: 'Roboto Bold' },
  { id: 'Roboto-Condensed', title: 'Roboto Condensed' },
  { id: 'Roboto-Light', title: 'Roboto Light' }
]

const postCountOptions = [
  { id: 0, title: 'Auto (1 per keyword)' },
  { id: 5, title: '5 posts' },
  { id: 10, title: '10 posts' },
  { id: 15, title: '15 posts' },
  { id: 20, title: '20 posts' },
  { id: 30, title: '30 posts' }
]

const form = reactive({
  name: defaultName,
  post_type: 'update',
  cta_type: 'learn_more',
  llm_provider: 'anthropic',
  image_provider: 'gemini_images',
  target_posts_count: 0,
  keywords: [...(props.defaultKeywords || [])],
  date_range_start: today,
  date_range_end: nextMonth,
  bar_color: '',
  bar_font: '',
  utm_params: { source: 'google', medium: 'organic', campaign: 'gbp-posts' },
  custom_instructions: ''
})

const effectiveCount = computed(() => {
  const target = parseInt(form.target_posts_count, 10) || 0
  return target > 0 ? target : form.keywords.length
})

const newKeyword = ref('')
const submitting = ref(false)
const error = ref(null)

function addKeyword() {
  const kw = newKeyword.value.trim()
  if (kw && !form.keywords.includes(kw)) {
    form.keywords.push(kw)
    newKeyword.value = ''
  }
}

function removeKeyword(i) {
  form.keywords.splice(i, 1)
}

async function submit() {
  if (form.keywords.length === 0) {
    error.value = 'Add at least one keyword'
    return
  }
  submitting.value = true
  error.value = null
  try {
    const { data } = await createGbpCampaign(props.locationId, { gbp_campaign: form })
    emit('created', data)
  } catch (e) {
    error.value = e.response?.data?.error || 'Failed to create campaign'
  } finally {
    submitting.value = false
  }
}
</script>
